import { components, paths } from "@/api-schema";
import {
  PathsV1RuleSearchPostParametersQueryExtend,
  PathsV1RuleSearchPostParametersQueryOrderByDirection,
  PathsV1RuleSearchPostParametersQueryOrderByField,
} from "@/api-schema-enums";
import { DEFAULT_PAGE_SIZE } from "@/common/api/_defaults";
import { useRuleDeleteMutation } from "@/common/api/rule/use-rule-delete.mutation";
import { useRuleRestoreMutation } from "@/common/api/rule/use-rule-restore.mutation";
import { useRuleSearchQuery } from "@/common/api/rule/use-rule-search.query";
import { RowWithIdentifier } from "@/components/data-table/_helpers/row-identifier";
import { DeletedStatus, DeletedStatusFilter } from "@/components/filter/predefined/deleted-status-filter";
import i18next from "i18next";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { createBasePredefinedTable } from "./_components/base-predefined-table";
import {
  BasePredefinedTableProps,
  DataFetchHooks,
  PredefinedTableConfig,
} from "./_components/base-predefined-table.types";
import { ruleTableDefaultColumns } from "./rule-table-columns";

type Rule = components["schemas"]["Rule"] & RowWithIdentifier;
type RuleSorting = NonNullable<paths["/v1/rule/search"]["post"]["parameters"]["query"]["orderBy"]>;
type RuleQueryParams = paths["/v1/rule/search"]["post"]["parameters"]["query"];
type RuleRequestBody = paths["/v1/rule/search"]["post"]["requestBody"]["content"]["application/json"];
type RuleExtend = NonNullable<paths["/v1/rule/search"]["post"]["parameters"]["query"]["extend"]>;

const DEFAULT_EXTEND: RuleExtend = [
  PathsV1RuleSearchPostParametersQueryExtend.Group,
  PathsV1RuleSearchPostParametersQueryExtend.Brands,
  PathsV1RuleSearchPostParametersQueryExtend.Branches,
  PathsV1RuleSearchPostParametersQueryExtend.Branches_Company,
  PathsV1RuleSearchPostParametersQueryExtend.Branches_Brands,
  PathsV1RuleSearchPostParametersQueryExtend.Branches_Address,
  PathsV1RuleSearchPostParametersQueryExtend.Companies,
  PathsV1RuleSearchPostParametersQueryExtend.CompanyGroups,
  PathsV1RuleSearchPostParametersQueryExtend.Departments,
  PathsV1RuleSearchPostParametersQueryExtend.JobFunctions,
  PathsV1RuleSearchPostParametersQueryExtend.Occupations,
] as const;

const DEFAULT_COLUMNS = [
  ruleTableDefaultColumns.columnCompanyGroups,
  ruleTableDefaultColumns.columnCompany,
  ruleTableDefaultColumns.columnBranches,
  ruleTableDefaultColumns.columnDepartment,
  ruleTableDefaultColumns.columnOccupation,
  ruleTableDefaultColumns.columnBrand,
  ruleTableDefaultColumns.columnJobFunctions,
  ruleTableDefaultColumns.columnDeletedAt,
];

const RULE_TABLE_CONFIG: PredefinedTableConfig<RuleSorting> = {
  defaultPageSize: DEFAULT_PAGE_SIZE,
  defaultSorting: [
    {
      direction: PathsV1RuleSearchPostParametersQueryOrderByDirection.Desc,
      field: PathsV1RuleSearchPostParametersQueryOrderByField.CreatedAt,
    },
  ] as const,
  defaultTableConfig: {
    searchPlaceholder: i18next.t("common:SearchRules"),
    features: {
      multiSelection: false,
      pagination: true,
      search: true,
    },
  } as const,
  sortingMapping: {
    createdAt: ["createdAt"],
    updatedAt: ["updatedAt"],
    deletedAt: ["deletedAt"],
    companies: ["companyDisplay"],
    companyGroups: ["companyGroupDisplay"],
    branches: ["branchDisplay"],
    brands: ["brandDisplay"],
    departments: ["departmentDisplay"],
    jobFunctions: ["jobFunctionDisplay"],
    occupations: ["occupationDisplay"],
  } as const,
  defaultRowActions: {
    showView: false,
    showDelete: true,
  } as const,
} as const;

const RULE_TABLE_QUERY_HOOKS: DataFetchHooks<Rule, RuleQueryParams, RuleRequestBody> = {
  useSearchQuery: useRuleSearchQuery,
  useDeleteMutation: useRuleDeleteMutation,
  useRestoreMutation: useRuleRestoreMutation,
} as const;

export interface RuleTableProps extends BasePredefinedTableProps<Rule, RuleSorting, RuleQueryParams, RuleRequestBody> {}

const BaseRuleTable = createBasePredefinedTable({
  config: RULE_TABLE_CONFIG,
  queryHooks: RULE_TABLE_QUERY_HOOKS,
  defaultColumns: DEFAULT_COLUMNS,
});

export function RuleTable(props: RuleTableProps) {
  const { t } = useTranslation(["common", "ram"]);
  const {
    name = "rule-table",
    showCreateButton = true,
    extend = DEFAULT_EXTEND,
    body = { deletedAt: [DeletedStatus.ACTIVE] },
    filters,
    filterValues,
    ...restProps
  } = props;

  const defaultFilters = useMemo(() => filters ?? [new DeletedStatusFilter()], [filters]);
  const defaultFilterValues = useMemo(() => filterValues ?? { deletedAt: [DeletedStatus.ACTIVE] }, [filterValues]);

  return (
    <BaseRuleTable
      searchPlaceholder={t("common:SearchRules")}
      name={name}
      showCreateButton={showCreateButton}
      extend={extend}
      body={body}
      filters={defaultFilters}
      filterValues={defaultFilterValues}
      confirmActions={["delete", "restore"]}
      toastMessages={{
        deleteSuccess: t("ram:RuleHasBeenDeleted"),
        deleteError: t("ram:ErrorDeletingRule"),
        restoreSuccess: t("ram:RuleHasBeenRestored"),
        restoreError: t("ram:ErrorRestoringRule"),
      }}
      confirmLabels={{
        delete: (row: Rule) => ({
          title: row.deletedAt ? t("common:PermanentlyDeleteRule") : t("common:DeleteRule"),
          text: row.deletedAt ? t("common:RuleWillBeDeletedPermanently") : t("common:RuleWillBeDeactivated"),
          confirmButton: row.deletedAt ? t("common:YesDeletePermanently") : t("ram:YesDelete"),
          cancelButton: t("common:Cancel"),
        }),
        restore: {
          title: t("common:RestoreRule"),
          text: t("common:DoYouReallyWantToRestoreThisRule"),
          confirmButton: t("common:YesRestore"),
          cancelButton: t("common:Cancel"),
        },
      }}
      {...restProps}
    />
  );
}
